import { Box, Button, Divider, Flex } from "@chakra-ui/react";
import React from "react";
import {
  CiGrid41,
  CiInboxOut,
  CiMemoPad,
  CiMenuKebab,
  CiText,
  CiVial,
  CiViewTable,
  CiWavePulse1,
} from "react-icons/ci";
import { LuMap } from "react-icons/lu";
import { useSearchParams } from "react-router";

import { useSearchState } from "@nens/react/hooks";

import { FilterWrapper } from "./components/filter-wrapper";
import { FrdTable } from "./features/frd";
import { GarTable } from "./features/gar";
import { GldTable } from "./features/gld";
import { GmnTable } from "./features/gmn";
import { GmwView } from "./features/gmw";
import { GpdTable } from "./features/gpd";
import { GufTable } from "./features/guf";
import { UploadTaskTable } from "./features/upload-task";
const viewTypeOptions = [
  "gmwMap",
  "gmwTable",
  "gmnTable",
  "gldTable",
  "gpdTable",
  "gufTable",
  "frdTable",
  "garTable",
  "uploadTasks",
] as const;
export type ViewType = (typeof viewTypeOptions)[number];

const Overview: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewType, setViewType] = useSearchState<ViewType>("viewType", "gmwMap", {
    searchParams,
    setSearchParams,
    clearOthers: "all",
  });

  const BroDomainButton = ({
    label,
    viewType: ownViewType,
    icon,
  }: {
    label: string;
    viewType: ViewType;
    icon: React.ReactElement;
  }) => {
    const onClick = () => {
      // Dont clear others meaning the filters of other view types, since the GMW map and table share filters
      const dontClearOthers =
        (ownViewType === "gmwMap" && viewType === "gmwTable") ||
        (ownViewType === "gmwTable" && viewType === "gmwMap");
      setViewType(ownViewType, { clearOthers: dontClearOthers ? [] : undefined });
    };
    return (
      <Button
        onClick={onClick}
        color={viewType === ownViewType ? "primary" : undefined}
        variant="ghost"
        justifyContent="flex-start"
        leftIcon={icon}
      >
        <Box w="fit-content" position="relative">
          {label}
          {viewType === ownViewType && (
            <Box h="1px" w="100%" position="absolute" bottom="-4px" left="0" bg="primary" />
          )}
        </Box>
      </Button>
    );
  };

  return (
    <Box flex={1} overflow="hidden" w="100%">
      <Flex h="full" maxH="full" flexDir={{ base: "column", md: "row" }} gap={5}>
        {/* Tabs */}
        <Box>
          <FilterWrapper />
          <Flex mx={5} flexDir="column" gap={2} justify="center">
            <BroDomainButton label="Overzichtskaart" viewType="gmwMap" icon={<LuMap />} />
            <BroDomainButton
              label="Leveringsoverzicht"
              viewType="uploadTasks"
              icon={<CiViewTable />}
            />
            <Divider borderColor="accent" />
            Grondwatermonitoring
            <Divider borderColor="accent" />
            <BroDomainButton label="GMN - Meetnetten" viewType="gmnTable" icon={<CiGrid41 />} />
            <BroDomainButton label="GMW - Peilbuizen" viewType="gmwTable" icon={<CiText />} />
            <BroDomainButton
              label="GLD - Grondwaterstanden"
              viewType="gldTable"
              icon={<CiWavePulse1 />}
            />
            <BroDomainButton
              label="FRD - Formatieweerstand"
              viewType="frdTable"
              icon={<CiMenuKebab />}
            />
            <BroDomainButton
              label="GAR - Grondwaterkwaliteit"
              viewType="garTable"
              icon={<CiVial />}
            />
            <Divider borderColor="accent" />
            Grondwatergebruik
            <Divider borderColor="accent" />
            <BroDomainButton
              label="GUF - Grondwatergebruiksysteem"
              viewType="gufTable"
              icon={<CiMemoPad />}
            />
            <BroDomainButton
              label="GPD - Grondwaterproductiedossier"
              viewType="gpdTable"
              icon={<CiInboxOut />}
            />
          </Flex>
        </Box>

        <Flex flexDir="column" h="full" w="full">
          {/* GMW (map or table) */}
          {(viewType === "gmwMap" || viewType === "gmwTable") && (
            <GmwView mode={viewType === "gmwMap" ? "map" : "table"} />
          )}

          {/* GMN table */}
          {viewType === "gmnTable" && <GmnTable />}

          {/* GLD table */}
          {viewType === "gldTable" && <GldTable />}

          {/* GPD table */}
          {viewType === "gpdTable" && <GpdTable />}

          {/* GUF table */}
          {viewType === "gufTable" && <GufTable />}

          {/* FRD table */}
          {viewType === "frdTable" && <FrdTable />}

          {/* GAR table */}
          {viewType === "garTable" && <GarTable />}

          {/* Upload task */}
          {viewType === "uploadTasks" && <UploadTaskTable />}
        </Flex>
      </Flex>
    </Box>
  );
};

export { Overview };
